import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else { setProfile(null); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function loadProfile(userId) {
    // 1. Try user_profiles first
    const { data: profile } = await supabase
      .from('user_profiles').select('*').eq('id', userId).single()
    if (profile) {
      // Phase 2: enforce is_active as a hard gate. A deactivated profile
      // is treated identically to "no profile at all" -- signed out
      // immediately, never allowed to reach the application shell.
      if (!profile.is_active) {
        await supabase.auth.signOut()
        setUser(null); setProfile(null); setLoading(false)
        return
      }
      setProfile(profile); setLoading(false); return
    }

    // 2. New user — check invited_users by email
    //
    // Bug fix: this used to query invited_users directly, but that
    // table's RLS only grants SELECT to is_platform_admin() -- a
    // brand-new (non-admin) user always got a 406/empty result here,
    // so their profile was never auto-created even with a genuine
    // invitation waiting, silently stalling sign-in forever.
    // get_my_invitation() is scoped server-side to return only the
    // caller's OWN invitation (matched against their own authenticated
    // email via auth.uid()), never anyone else's -- authUser.email is
    // no longer needed as an explicit parameter for that same reason.
    {
      const { data: invitedRows } = await supabase.rpc('get_my_invitation')
      const invited = invitedRows?.[0]
      if (invited) {
        // Auto-create profile from invitation. Server-side guaranteed by
        // the user_profiles_insert_self_if_invited RLS policy (Phase 2):
        // this INSERT can only succeed if auth.uid() = id AND a matching
        // invited_users row still exists -- not merely a client-side
        // convention. organization_id comes exclusively from the
        // invitation record itself (set by the inviting admin in
        // UsersPage.jsx) -- never inferred from the email domain or any
        // other source.
        const { data: newProfile, error: insertErr } = await supabase.from('user_profiles').insert({
          id: userId,
          email: invited.email,
          full_name: invited.full_name,
          role: invited.role,
          organization_id: invited.organization_id,
          is_active: true
        }).select().single()
        if (insertErr) {
          // RLS rejected the insert (e.g. invitation was consumed by a
          // concurrent request, or email mismatch) -- fail closed.
          setProfile(null); setLoading(false); return
        }
        // Apply any access the inviting admin predefined (project
        // memberships, Estimation roles, additional permissions) via
        // the secure RPC -- never a direct insert from here. The RPC
        // resolves the caller's own verified email server-side, so
        // this can only ever apply access that was actually predefined
        // for this exact account.
        await supabase.rpc('activate_invitation_access')
        // Remove from invited_users
        await supabase.from('invited_users').delete().eq('email', invited.email)
        setProfile(newProfile || null)
        setLoading(false)
        return
      }
    }
    setProfile(null)
    setLoading(false)
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return error
  }

  async function signUp(email, password) {
    // Neutral sign-up: the form never reveals whether an email is invited.
    // The invitation is enforced server-side at profile creation.
    const { error } = await supabase.auth.signUp({ email, password })
    return error
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    })
    return error
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null); setProfile(null)
  }

  const role = profile?.role || 'viewer'

  // 'full_system' is a dedicated legacy-bridge role (never shown as a
  // real job title anywhere) introduced specifically for users
  // provisioned through the Quick Add Cost Control Engineer flow. It
  // exists only so the legacy `can.X` checks below -- which still
  // gate real screens/buttons across the app -- correctly recognize
  // those users, instead of silently falling back to `viewer` (the
  // column's own DB default) because onboard_cost_control_engineer()
  // only ever writes the newer role_permissions-based access, never
  // this legacy column. It is deliberately included in every
  // operational permission below and excluded from the two
  // administrative ones, mirroring exactly the access boundary already
  // enforced server-side by role_permissions for company_operator.
  const can = {
    approve_po:          ['admin','director_operations','director_commercial','pm','cost_control','finance','full_system'].includes(role),
    approve_ipc:         ['admin','director_operations','director_commercial','pm','cost_control','full_system'].includes(role),
    approve_subcontract: ['admin','director_operations','director_commercial','pm','cost_control','finance','full_system'].includes(role),
    approve_payroll:     ['admin','cost_control','finance','full_system'].includes(role),
    edit_budget:         ['admin','cost_control','director_commercial','full_system'].includes(role),
    view_reports:        ['admin','director_operations','director_commercial','pm','cost_control','finance','full_system'].includes(role),
    manage_users:        ['admin'].includes(role),
    add_po:              ['admin','procurement','pm','cost_control','site_engineer','full_system'].includes(role),
    add_ipc:             ['admin','pm','cost_control','site_engineer','full_system'].includes(role),
    is_director:         ['admin','director_operations','director_commercial','full_system'].includes(role),
    is_admin:            role === 'admin',
    // Inventory Transfer workflow (corrected 2026-08-13): single approval
    // stage, Cost Control only -- no Director escalation regardless of
    // value (Transfer is Cost-Neutral, not a new financial commitment, so
    // the PO/Subcontracts two-stage threshold model does not apply).
    // approve_transfer is intentionally its OWN permission, not a reuse of
    // approve_po -- approve_po includes director_operations/
    // director_commercial/pm/finance, none of whom should approve a
    // transfer under this corrected single-stage model.
    add_transfer:        ['admin','storekeeper','pm','site_engineer','full_system'].includes(role),
    approve_transfer:    ['admin','cost_control','full_system'].includes(role),
    receive_transfer:    ['admin','storekeeper','pm','site_engineer','full_system'].includes(role),
  }

  return (
    <AuthContext.Provider value={{ user, profile, role, loading, can, signIn, signUp, signOut, loadProfile, resetPassword }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
